import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type UserOut } from "../api";
import { BrandWordmark } from "../components/brand";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/**
 * Majburiy parol almashtirish ekrani. Admin/bot parolni qayta tiklaganda
 * `must_change_password=true` bo'ladi: bunday holatda foydalanuvchi tariff/branch
 * kabi himoyalangan endpointlarga kira olmaydi (backend `password_change_required`
 * → 401 qaytaradi). Shu sababli Shell mount qilinmasdan oldin shu ekran ko'rsatiladi.
 */
// Backend bilan bir xil minimal uzunlik (auth.py MIN_PASSWORD_LENGTH).
const MIN_PASSWORD_LENGTH = 8;

export function ChangePassword({
  onDone,
  onLogout,
}: {
  onDone: (u: UserOut) => void;
  onLogout: () => void;
}) {
  const { t } = useTranslation(["login", "common"]);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (next.length < MIN_PASSWORD_LENGTH) {
      setErr(t("changePassword.tooShort"));
      return;
    }
    if (next !== confirm) {
      setErr(t("changePassword.mismatch"));
      return;
    }
    setBusy(true);
    try {
      const updated = await api.changePassword(current, next, confirm);
      onDone(updated);
    } catch (e) {
      const er = e as { code?: string; message?: string };
      setErr(
        er.code === "invalid_current_password" || er.code === "invalid_credentials"
          ? t("changePassword.wrongCurrent")
          : er.message || t("changePassword.failed"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-sm shadow-lg">
        <CardContent className="pt-8 pb-7 space-y-6">
          <div className="flex flex-col items-center gap-3">
            <div className="size-14 rounded-[8px] bg-[#0A0E18] p-[3px] shadow-lg shadow-primary/25">
              <img
                src="/logo-mark.png"
                alt="AvtoDaftar"
                className="w-full h-full object-cover rounded-[5px]"
              />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight">
                <BrandWordmark />
              </h1>
              <p className="text-sm text-muted-foreground">
                {t("changePassword.subtitle")}
              </p>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current">{t("changePassword.current")}</Label>
              <Input
                id="current"
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="••••••••"
                required
                autoFocus
                autoComplete="current-password"
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">{t("changePassword.new")}</Label>
              <Input
                id="new-password"
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">
                {t("changePassword.confirm")}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                disabled={busy}
              />
            </div>
            {err && (
              <div className="text-sm rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-2">
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full" size="lg">
              {busy ? t("changePassword.submitting") : t("changePassword.submit")}
            </Button>
            <button
              type="button"
              className="w-full text-sm text-muted-foreground hover:text-foreground"
              onClick={onLogout}
              disabled={busy}
            >
              {t("changePassword.cancel")}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
